import { useQueryClient } from '@tanstack/react-query';
import { QrCode } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  type CreateTopupResponse,
  TOPUP_MAX_CNY_CENTS,
  TOPUP_MIN_CNY_CENTS,
  type TopupPayType,
} from '@nadoutong/contracts';
import { PlatformApiError } from '../api/client.ts';
import {
  BALANCE_QUERY_KEY,
  CREDITS_QUERY_KEY,
  TRANSACTIONS_PREFIX,
  useCreateTopup,
  useTopupQuote,
  useTopupStatus,
} from '../api/useBilling.ts';
import { coerceErrorMessage } from '../components/Toast.tsx';
import { formatCnyCents } from '../components/formatters.ts';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Panel,
  RadioGroup,
  StatusLamp,
  buttonClass,
} from '../ui/index.ts';

/**
 * `/billing/topup` — tenant-facing topup form (Epic #84 W4 P9 / P17
 * CNY-integer-first semantics), re-homed onto the Dispatch Deck system
 * (epic #897 PR 9).
 *
 * Flow:
 *   1. User picks an amount (CNY whole yuan, ¥1–¥1500) + payment method.
 *   2. On submit we POST `/v1/billing/topup` with `{ amountCnyCents,
 *      payType }` and branch on the shape of the response:
 *        - `qrCodeUrl` / `imageUrl` present (PRIMARY PATH after P18) →
 *          open a `ui/Dialog` that shows the QR + polls
 *          `/v1/billing/topup/:id` every 3s until the status leaves
 *          `pending`. Tenant scans with their phone's WeChat app and
 *          stays inside console-web.
 *        - `payUrl` present (fallback) → desktop hosted cashier,
 *          redirect via `window.location.href`. Only hit when the
 *          provider couldn't produce a QR.
 *
 * Why CNY-integer-first (not USD): the payment aggregator (ZPay)
 * operates in CNY and merchant reconciliation is in whole yuan; the
 * USD ledger amount is derived server-side at order time.
 *
 * Errors are single-channel (epic #897 AC 7): field validation renders
 * under the amount field; submit/provider failures render inline above
 * the actions row. Nothing double-reports through the toast.
 */

const DEFAULT_CNY_CENTS = 1_000; // ¥10
const DEFAULT_CNY_YUAN_TEXT = '10';
const PAY_TYPE_OPTIONS: readonly { value: TopupPayType; labelKey: 'topup.pay_type.wxpay' }[] = [
  { value: 'wxpay', labelKey: 'topup.pay_type.wxpay' },
];

/**
 * Ceiling on the QR dialog's polling window. After this many ms with no
 * terminal status we stop polling and show a "taking longer than usual"
 * inline message alongside the QR (we keep the QR visible — the user
 * might still pay via it). Matches the `PENDING_CEILING_MS` on the
 * return page; both exist so an unattended tab stops burning
 * `GET /v1/billing/topup/:id` requests forever.
 */
export const QR_POLL_CEILING_MS = 60_000;

export function BillingTopupPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const createM = useCreateTopup();
  const t = useT();
  const { user } = useSession();
  const canManageBilling = hasPermission(user, 'org:billing:manage');

  const [amountCnyCents, setAmountCnyCents] = useState<number>(DEFAULT_CNY_CENTS);
  const [amountText, setAmountText] = useState<string>(DEFAULT_CNY_YUAN_TEXT);
  const [payType, setPayType] = useState<TopupPayType>('wxpay');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [qrOrder, setQrOrder] = useState<CreateTopupResponse | null>(null);

  const quoteQ = useTopupQuote(amountCnyCents, {
    enabled:
      canManageBilling &&
      amountCnyCents > 0 &&
      amountCnyCents % 100 === 0 &&
      amountCnyCents >= TOPUP_MIN_CNY_CENTS &&
      amountCnyCents <= TOPUP_MAX_CNY_CENTS,
  });

  function handleAmountChange(raw: string): void {
    setAmountText(raw);
    setSubmitError(null);
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      setAmountCnyCents(0);
      setValidationError(null);
      return;
    }
    // Only accept a positive integer — whole yuan. A dot, comma, or any
    // non-digit character is a fast reject before any business bound
    // check. The server stays the final authority on the range and
    // whole-yuan rules (we match them eagerly for UX).
    if (!/^\d+$/.test(trimmed)) {
      setValidationError(t('topup.error.whole_yuan'));
      return;
    }
    const yuan = Number(trimmed);
    if (!Number.isFinite(yuan) || yuan <= 0) {
      setValidationError(t('topup.error.positive'));
      return;
    }
    setAmountCnyCents(yuan * 100);
    setValidationError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (createM.isPending) {
      return;
    }
    setSubmitError(null);
    if (amountCnyCents <= 0) {
      setValidationError(t('topup.error.required'));
      return;
    }
    if (amountCnyCents % 100 !== 0) {
      // Defensive — the input validator should have caught this already.
      setValidationError(t('topup.error.no_cents'));
      return;
    }
    if (amountCnyCents < TOPUP_MIN_CNY_CENTS) {
      setValidationError(t('topup.error.min', { amount: formatCnyCents(TOPUP_MIN_CNY_CENTS) }));
      return;
    }
    if (amountCnyCents > TOPUP_MAX_CNY_CENTS) {
      setValidationError(t('topup.error.max', { amount: formatCnyCents(TOPUP_MAX_CNY_CENTS) }));
      return;
    }
    setValidationError(null);
    try {
      const order = await createM.mutateAsync({ amountCnyCents, payType });
      // Response priority matches the provider (see
      // `packages/payments/src/zpay/provider.ts`): scan-to-pay QR is the
      // primary UX and keeps the tenant inside console-web; `payUrl` is
      // only surfaced when the provider couldn't produce a QR — in that
      // case we fall back to a browser redirect so the tenant isn't
      // blocked.
      if (
        (typeof order.qrCodeUrl === 'string' && order.qrCodeUrl.length > 0) ||
        (typeof order.imageUrl === 'string' && order.imageUrl.length > 0)
      ) {
        setQrOrder(order);
        return;
      }
      if (typeof order.payUrl === 'string' && order.payUrl.length > 0) {
        window.location.href = order.payUrl;
        return;
      }
      // Backend returned neither — should never happen given the
      // contract, but fail soft with an inline submit error.
      setSubmitError(t('topup.error.no_redirect'));
    } catch (err) {
      setSubmitError(
        err instanceof PlatformApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err),
      );
    }
  }

  function handleDialogClose(): void {
    setQrOrder(null);
    // User may have paid in the ZPay app already; nudge a refresh so
    // the /billing view catches up without a manual reload.
    void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: TRANSACTIONS_PREFIX });
  }

  if (!canManageBilling) {
    return (
      <section data-testid="billing-topup-page" className="flex flex-col gap-4">
        <PageHeader title={t('topup.title')} sub={t('topup.subtitle')} testId="billing-topup-header" />
        <div
          data-testid="billing-topup-forbidden"
          className="rounded-lg border border-line-soft bg-card"
        >
          <EmptyState
            title={t('topup.forbidden.title')}
            hint={t('topup.forbidden.body')}
            action={
              <Link
                to="/billing"
                className={buttonClass('secondary')}
                data-testid="billing-topup-forbidden-cta"
              >
                {t('topup.forbidden.back')}
              </Link>
            }
          />
        </div>
      </section>
    );
  }

  return (
    <section data-testid="billing-topup-page" className="flex flex-col gap-4">
      <PageHeader title={t('topup.title')} sub={t('topup.subtitle')} testId="billing-topup-header" />

      <Panel caption={t('topup.form.caption')} padded testId="billing-topup-card" className="max-w-2xl">
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          noValidate
        >
          <FormField
            label={t('topup.amount.label')}
            fieldId="billing-topup-amount"
            hint={t('topup.amount.hint', {
              min: formatCnyCents(TOPUP_MIN_CNY_CENTS),
              max: formatCnyCents(TOPUP_MAX_CNY_CENTS),
            })}
            error={
              validationError !== null ? (
                <span data-testid="billing-topup-error">{validationError}</span>
              ) : undefined
            }
          >
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="font-mono text-body text-paper-mid"
                data-testid="billing-topup-amount-prefix"
              >
                ¥
              </span>
              <Input
                id="billing-topup-amount"
                mono
                inputMode="numeric"
                pattern="\d*"
                value={amountText}
                onChange={(e) => handleAmountChange(e.currentTarget.value)}
                disabled={createM.isPending}
                invalid={validationError !== null}
                aria-describedby={
                  validationError !== null ? 'billing-topup-amount-error' : undefined
                }
                placeholder="10"
                data-testid="billing-topup-amount"
              />
            </div>
          </FormField>

          <div className="flex flex-col gap-1.5">
            <span className="text-label text-paper-mid">{t('topup.usd.label')}</span>
            <div
              className="rounded border border-line-soft bg-well px-3 py-2 font-mono text-title text-paper"
              data-testid="billing-topup-usd-display"
            >
              {quoteQ.isSuccess ? `≈ ${formatCnyCents(quoteQ.data.amountCnyCents)}` : '—'}
            </div>
            <p className="m-0 text-label text-paper-low">
              {quoteQ.isSuccess
                ? t('topup.usd.hint', { rate: quoteQ.data.appliedRate.toFixed(4) })
                : quoteQ.isError
                  ? t('topup.usd.hint_unavailable')
                  : t('topup.usd.hint_loading')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-label text-paper-mid">{t('topup.pay_type.legend')}</span>
            <RadioGroup
              value={payType}
              onValueChange={(next) => {
                if (next === 'wxpay') {
                  setPayType(next);
                }
              }}
              options={PAY_TYPE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(opt.labelKey),
                disabled: createM.isPending,
              }))}
              ariaLabel={t('topup.pay_type.legend')}
              testId="billing-topup-pay-type"
            />
          </div>

          {submitError !== null ? (
            <p
              role="alert"
              className="m-0 text-label text-bad"
              data-testid="billing-topup-submit-error"
            >
              {submitError}
            </p>
          ) : null}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Link
              to="/billing"
              className={buttonClass('ghost')}
              data-testid="billing-topup-cancel"
            >
              {t('topup.cancel')}
            </Link>
            <Button
              type="submit"
              variant="primary"
              loading={createM.isPending}
              disabled={amountCnyCents <= 0}
              data-testid="billing-topup-submit"
            >
              {t('topup.submit')}
            </Button>
          </div>

          <p className="m-0 text-label text-paper-low">{t('topup.fee_hint')}</p>
        </form>
      </Panel>

      {qrOrder !== null ? (
        <TopupQrDialog
          order={qrOrder}
          onClose={handleDialogClose}
          onPaid={() => {
            // Balance + tx queries also refresh on the return page, but
            // nudge here too so the tenant sees updated numbers after
            // closing the dialog.
            void qc.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
            void qc.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
            void qc.invalidateQueries({ queryKey: TRANSACTIONS_PREFIX });
            navigate(`/billing/topup/return?order_id=${encodeURIComponent(qrOrder.orderId)}`);
          }}
        />
      ) : null}
    </section>
  );
}

interface TopupQrDialogProps {
  readonly order: CreateTopupResponse;
  readonly onClose: () => void;
  readonly onPaid: () => void;
}

function TopupQrDialog({ order, onClose, onPaid }: TopupQrDialogProps): JSX.Element {
  const t = useT();
  const [pollEnabled, setPollEnabled] = useState(true);
  const statusQ = useTopupStatus(order.orderId, { enabled: pollEnabled });
  const status = statusQ.data?.status ?? 'pending';
  const paidFiredRef = useRef(false);

  // Ceiling on the polling window: after `QR_POLL_CEILING_MS` with no
  // terminal status, stop polling and surface a "taking longer than
  // usual" inline hint next to the QR. The QR stays visible because the
  // user might still be completing payment in their phone app; we just
  // refuse to burn requests while they do.
  useEffect(() => {
    const handle = setTimeout(() => {
      setPollEnabled(false);
    }, QR_POLL_CEILING_MS);
    return () => clearTimeout(handle);
  }, []);

  const slowMode = !pollEnabled && status === 'pending';

  // Fire `onPaid` exactly once when the status transitions to paid.
  // `useTopupStatus` already stops polling on any non-pending value.
  useEffect(() => {
    if (status === 'paid' && !paidFiredRef.current) {
      paidFiredRef.current = true;
      onPaid();
    }
  }, [status, onPaid]);

  const qrTarget =
    typeof order.imageUrl === 'string' && order.imageUrl.length > 0 ? order.imageUrl : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title={t('topup.qr.title')}
      description={
        <>
          {t('topup.qr.order_prefix')}
          <span className="font-mono text-paper">{order.orderId}</span>
        </>
      }
      closeLabel={t('topup.qr.close')}
      testId="billing-topup-qr-modal"
      footer={
        <Button variant="secondary" onClick={onClose} data-testid="billing-topup-qr-close">
          {t('topup.qr.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-4 rounded-lg bg-well p-6">
          {qrTarget !== null ? (
            <img
              src={qrTarget}
              alt={t('topup.qr.image_alt')}
              className="h-56 w-56 rounded-lg bg-paper p-2"
              data-testid="billing-topup-qr-image"
            />
          ) : (
            <div
              className="flex h-56 w-56 flex-col items-center justify-center gap-3 rounded-lg border border-line-soft bg-card text-center"
              data-testid="billing-topup-qr-fallback"
            >
              <QrCode aria-hidden="true" className="h-10 w-10 text-signal" />
              <p className="m-0 px-3 text-label leading-snug text-paper-low">
                {t('topup.qr.fallback_hint')}
              </p>
            </div>
          )}
          {order.qrCodeUrl !== undefined && order.qrCodeUrl.length > 0 ? (
            <p
              className="m-0 break-all font-mono text-micro text-paper-low"
              data-testid="billing-topup-qr-url"
            >
              {order.qrCodeUrl}
            </p>
          ) : null}
        </div>

        <div
          className="flex items-center gap-2 rounded border border-line-soft bg-card px-3 py-2"
          data-testid="billing-topup-qr-status"
          role="status"
        >
          <StatusLamp
            tone={status === 'paid' ? 'ok' : slowMode ? 'warn' : 'signal'}
            pulse={status === 'pending' && pollEnabled}
          />
          <span className="text-dense text-paper">
            {status === 'paid'
              ? t('topup.qr.status.paid')
              : status !== 'pending'
                ? t('topup.qr.status.other', { status })
                : pollEnabled
                  ? t('topup.qr.status.pending')
                  : t('topup.qr.status.slow')}
          </span>
        </div>

        {slowMode ? (
          <p
            className="m-0 rounded border border-line-soft bg-card px-3 py-2 text-label leading-snug text-paper-low"
            data-testid="billing-topup-qr-slow"
          >
            {t('topup.qr.slow_notice')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
